import React, { useEffect, useState } from "react";
import { assetUrl, getPosters } from "../lib/api";

// Renders top banner, carousel section, and popup based on active posters from /api/posters
// Pass `slots` (array subset of ['banner','carousel','popup']) to render only some parts.
export default function PosterDisplay({ slots = ["banner", "carousel", "popup"] } = {}) {
  const [banners, setBanners] = useState([]);
  const [carousel, setCarousel] = useState([]);
  const [popups, setPopups] = useState([]);
  const [activePopup, setActivePopup] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    getPosters().then((r) => {
      const list = r.posters || [];
      setBanners(list.filter((p) => p.kind === "banner"));
      setCarousel(list.filter((p) => p.kind === "carousel"));
      const pops = list.filter((p) => p.kind === "popup");
      setPopups(pops);
      if (pops.length > 0) {
        const seen = localStorage.getItem(`rw_popup_${pops[0].id}`);
        if (!seen) setActivePopup(pops[0]);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (carousel.length <= 1) return;
    const t = setInterval(() => setCarouselIdx((i) => (i + 1) % carousel.length), 5000);
    return () => clearInterval(t);
  }, [carousel.length]);

  const dismissPopup = () => {
    if (activePopup) localStorage.setItem(`rw_popup_${activePopup.id}`, "1");
    setActivePopup(null);
  };

  return (
    <>
      {/* Banner strip */}
      {slots.includes("banner") && banners.map((b) => (
        <a
          key={b.id}
          href={b.link_url || "#"}
          onClick={(e) => { if (!b.link_url) e.preventDefault(); }}
          className="block bg-rw-ink text-white"
          data-testid={`poster-banner-${b.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center gap-4">
            <img src={assetUrl(b.image_url)} alt={b.title || "Banner"} className="h-8 w-auto rounded" />
            <div className="flex-1 min-w-0">
              {b.title && <div className="font-display text-sm md:text-base truncate">{b.title}</div>}
              {b.subtitle && <div className="text-xs text-white/70 truncate">{b.subtitle}</div>}
            </div>
            {b.link_url && <span className="text-xs text-rw-blue">→</span>}
          </div>
        </a>
      ))}

      {/* Carousel section */}
      {slots.includes("carousel") && carousel.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="poster-carousel-section">
          <div className="relative rounded-2xl overflow-hidden border border-rw-ink/10 bg-rw-bg2">
            {carousel.map((p, i) => (
              <a
                key={p.id}
                href={p.link_url || "#"}
                onClick={(e) => { if (!p.link_url) e.preventDefault(); }}
                className={`block transition-opacity duration-500 ${i === carouselIdx ? "opacity-100" : "opacity-0 absolute inset-0"}`}
                data-testid={`poster-carousel-${p.id}`}
              >
                <div className="grid md:grid-cols-2 items-stretch">
                  <img src={assetUrl(p.image_url)} alt={p.title || "Poster"} className="w-full h-64 md:h-80 object-cover" />
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    {p.title && <h3 className="font-display text-3xl md:text-4xl leading-tight">{p.title}</h3>}
                    {p.subtitle && <p className="mt-3 text-rw-ink2">{p.subtitle}</p>}
                    {p.link_url && <span className="mt-6 self-start btn-dark rounded-full px-5 py-2.5 text-sm font-semibold">Learn more →</span>}
                  </div>
                </div>
              </a>
            ))}
            {carousel.length > 1 && (
              <div className="absolute bottom-4 right-4 flex gap-2">
                {carousel.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCarouselIdx(i)}
                    className={`w-2.5 h-2.5 rounded-full ${i === carouselIdx ? "bg-rw-ink" : "bg-rw-ink/30"}`}
                    aria-label={`Show poster ${i + 1}`}
                    data-testid={`poster-carousel-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Popup modal */}
      {slots.includes("popup") && activePopup && (
        <div
          className="fixed inset-0 z-[100] bg-rw-ink/70 backdrop-blur flex items-center justify-center p-4"
          onClick={dismissPopup}
          data-testid={`poster-popup-${activePopup.id}`}
        >
          <div className="relative max-w-lg w-full bg-rw-bg2 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={dismissPopup}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-rw-ink z-10"
              aria-label="Close"
              data-testid="poster-popup-close"
            >
              ✕
            </button>
            <img src={assetUrl(activePopup.image_url)} alt={activePopup.title || "Offer"} className="w-full max-h-72 object-cover" />
            <div className="p-6">
              {activePopup.title && <h3 className="font-display text-2xl">{activePopup.title}</h3>}
              {activePopup.subtitle && <p className="mt-2 text-rw-ink2 text-sm">{activePopup.subtitle}</p>}
              <div className="mt-5 flex gap-3">
                {activePopup.link_url && (
                  <a href={activePopup.link_url} className="btn-dark rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="poster-popup-cta">
                    Grab this →
                  </a>
                )}
                <button onClick={dismissPopup} className="rounded-full border border-rw-ink/20 px-5 py-2.5 text-sm text-rw-ink2 hover:bg-rw-bg3">
                  No thanks
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
